import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { tableRows, bold } from "../../utils/xlsx";
import { download, ExportButton, fileName } from "./excel";
import { printTable } from "../../utils/printTable";

const todayIso = () => new Date().toISOString().slice(0, 10);
const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const COLUMNS = [
  { key: "skuCode", label: "SKU Code" },
  { key: "productName", label: "Product" },
  { key: "warehouseName", label: "Warehouse" },
  { key: "closingQuantity", label: "Closing Qty", format: "qty" },
  { key: "units", label: "Units" },
  { key: "taxPercent", label: "Tax %", format: "qty", total: false },
  { key: "mrp", label: "MRP", format: "money", total: false },
  { key: "ratePerUnit", label: "Rate / Unit", format: "money", total: false },
  { key: "grossRateInclTax", label: "Gross Rate (incl. tax)", format: "money", total: false },
  { key: "valueBeforeTax", label: "Value Before Tax", format: "money" },
  { key: "valueAfterTax", label: "Value After Tax", format: "money" },
];

/**
 * Closing stock stored at 11:59 PM every day, per product and warehouse — pick a date to see that day's closing stock with its
 * rate and values. "Store today's now" takes today's snapshot on demand (the night job overwrites it).
 */
const ClosingStockHistory = () => {
  const [date, setDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null);

  const load = async (forDate = date) => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/inventory/closing-stock-history", { date: forDate });
      setRows(Array.isArray(data) ? data : []);
      setApplied(forDate);
    } catch (error) {
      toast.error(error.message || "Failed to load the closing stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snapshot = async () => {
    setLoading(true);
    try {
      const result = await api.post("/api/v1/inventory/closing-stock-history/snapshot", {});
      toast.success(`Today's closing stock stored (${result?.rows ?? 0} rows)`);
      setDate(todayIso());
      await load(todayIso());
    } catch (error) {
      toast.error(error.message || "Could not store the closing stock");
      setLoading(false);
    }
  };

  const total = (key) => rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading...</div>
          </div>
        </div>
      ) : null}
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Closing stock of every product in every warehouse, stored automatically at 11:59 PM each day. Rate is the purchase price per unit
        (the selling price when no purchase price is known); values before and after tax are quantity × rate and quantity × gross rate.
      </div>
      <div className="filters-bar">
        <input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} aria-label="Date" />
        <button className="primary" onClick={() => load()} disabled={loading}>
          Show
        </button>
        <button className="secondary" onClick={snapshot} disabled={loading} title="Store today's closing stock now">
          Store today's now
        </button>
        <ExportButton
          disabled={!rows.length}
          onClick={() =>
            download(fileName("Closing Stock", applied), [
              { name: "Closing Stock", rows: [[bold(`Closing Stock — ${applied}`)], [], ...tableRows(COLUMNS, rows, { totals: true })] },
            ])
          }
        />
        <button className="secondary" disabled={!rows.length} onClick={() => printTable("Closing Stock", applied, COLUMNS, rows, { totals: true })}>
          Print / PDF
        </button>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} style={c.format ? right : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <div className="empty-state">
                    <span className="empty-state-title">No closing stock stored for {applied || "this date"}</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={`${row.productId}-${row.warehouseId}`}>
                  <td>{row.skuCode}</td>
                  <td>{row.productName}</td>
                  <td>{row.warehouseName}</td>
                  <td style={right}>{qty(row.closingQuantity)}</td>
                  <td>{row.units}</td>
                  <td style={right}>{qty(row.taxPercent)}</td>
                  <td style={right}>{money(row.mrp)}</td>
                  <td style={right}>{money(row.ratePerUnit)}</td>
                  <td style={right}>{money(row.grossRateInclTax)}</td>
                  <td style={right}>{money(row.valueBeforeTax)}</td>
                  <td style={right}>{money(row.valueAfterTax)}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length ? (
            <tfoot>
              <tr>
                <td colSpan={3} style={{ fontWeight: 700 }}>
                  Total
                </td>
                <td style={{ ...right, fontWeight: 700 }}>{qty(total("closingQuantity"))}</td>
                <td colSpan={5} />
                <td style={{ ...right, fontWeight: 700 }}>{money(total("valueBeforeTax"))}</td>
                <td style={{ ...right, fontWeight: 700 }}>{money(total("valueAfterTax"))}</td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default ClosingStockHistory;
